import React, { FC, ReactNode } from 'react';
import { CheckIcon } from '@heroicons/react/24/solid';
import cx from 'classnames';
import { APP_ACCENT, APP_BORDER } from '@/constant/theme/colors.constant';
import { Text } from '../atoms/AppText';
import AppCheckbox from '../atoms/AppCheckbox';
import AppRadio from '../atoms/AppRadio';
import type { CheckboxColor } from '../atoms/AppCheckbox';

// ─── Types ────────────────────────────────────────────────────────────────────

export type SelectableChipTheme =
  | 'blue'
  | 'purple'
  | 'green'
  | 'orange'
  | 'red'
  | 'primary';

export type SelectableChipSelectionType = 'default' | 'checkbox' | 'radio';

export interface SelectableChipProps {
  /** Display label */
  label: string;
  /** Whether the chip is currently selected */
  selected: boolean;
  /** Called when the chip is clicked */
  onClick: () => void;
  /** Colour theme for active/inactive states */
  theme?: SelectableChipTheme;
  /** Selection indicator style — default: check icon */
  selectionType?: SelectableChipSelectionType;
  /** Optional leading icon (overrides the selection indicator) */
  icon?: ReactNode;
  /** Chip is non-interactive */
  disabled?: boolean;
  /** Extra Tailwind classes on the outer button */
  className?: string;
  /** data-testid forwarded to the button element */
  testId?: string;
}

// ─── Theme tokens ─────────────────────────────────────────────────────────────

const CHIP_THEME: Record<
  SelectableChipTheme,
  { active: string; inactive: string; color: CheckboxColor }
> = {
  blue: {
    active: `${APP_ACCENT.blue.bg} ${APP_ACCENT.blue.border} text-white scale-[1.03]`,
    inactive: `bg-white dark:bg-gray-700 ${APP_BORDER.default} text-gray-600 dark:text-gray-300 hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30`,
    color: 'blue',
  },
  purple: {
    active: `${APP_ACCENT.purple.bg} ${APP_ACCENT.purple.border} text-white scale-[1.03]`,
    inactive: `bg-white dark:bg-gray-700 ${APP_BORDER.default} text-gray-600 dark:text-gray-300 hover:border-purple-400 hover:text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/30`,
    color: 'purple',
  },
  green: {
    active: `${APP_ACCENT.green.bg} ${APP_ACCENT.green.border} text-white scale-[1.03]`,
    inactive: `bg-white dark:bg-gray-700 ${APP_BORDER.default} text-gray-600 dark:text-gray-300 hover:border-green-400 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/30`,
    color: 'green',
  },
  orange: {
    active: `${APP_ACCENT.orange.bg} ${APP_ACCENT.orange.border} text-white scale-[1.03]`,
    inactive: `bg-white dark:bg-gray-700 ${APP_BORDER.default} text-gray-600 dark:text-gray-300 hover:border-orange-400 hover:text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-900/30`,
    color: 'orange',
  },
  red: {
    active: `${APP_ACCENT.red.bg} ${APP_ACCENT.red.border} text-white scale-[1.03]`,
    inactive: `bg-white dark:bg-gray-700 ${APP_BORDER.default} text-gray-600 dark:text-gray-300 hover:border-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30`,
    color: 'red',
  },
  primary: {
    active: 'bg-primary border-primary text-white scale-[1.03]',
    inactive: `bg-white dark:bg-gray-700 ${APP_BORDER.default} text-gray-600 dark:text-gray-300 hover:border-primary hover:text-primary hover:bg-primary/5 dark:hover:bg-primary/10`,
    color: 'primary',
  },
};

// ─── Component ────────────────────────────────────────────────────────────────

const SelectableChip: FC<SelectableChipProps> = ({
  label,
  selected,
  onClick,
  theme = 'primary',
  selectionType = 'default',
  icon,
  disabled = false,
  className,
  testId,
}) => {
  const t = CHIP_THEME[theme];

  const indicator = icon ? (
    <span className="flex-shrink-0 w-3 h-3">{icon}</span>
  ) : selectionType === 'checkbox' ? (
    <AppCheckbox
      checked={selected}
      onChange={() => {}}
      size="xs"
      color={t.color}
      variant="filled"
      inverted={selected}
      className="pointer-events-none"
    />
  ) : selectionType === 'radio' ? (
    <AppRadio
      checked={selected}
      onChange={() => {}}
      size="xs"
      color={t.color}
      variant="filled"
      inverted={selected}
      className="pointer-events-none"
    />
  ) : selected ? (
    <CheckIcon className="w-3 h-3 flex-shrink-0" />
  ) : (
    <span className="w-3 h-3 flex-shrink-0 rounded-full border border-current opacity-25" />
  );

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      data-testid={testId ?? ''}
      className={cx(
        'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full',
        'border transition-all duration-150 select-none',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1',
        'disabled:opacity-40 disabled:pointer-events-none',
        selected ? t.active : t.inactive,
        className,
      )}
    >
      {indicator}
      <Text
        as="span"
        size="md"
        weight="semibold"
        color="inherit"
        tracking="wide"
      >
        {label}
      </Text>
    </button>
  );
};

export default SelectableChip;
